// src/Pages/Candidates.jsx
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CircleCheck, Trash2, SortAsc, Users, AlertCircle, Menu } from "lucide-react";
import Sidebar from "../Components/Sidebar";
import CandidateList from "../Components/CandidateList";
import BottomBar from "../Components/BottomBar";
import TopBar from "../Components/TopBar";
import { toast, Toaster } from "react-hot-toast";
import { getAllElections } from "../services/ElectionService";
import { getCandidatesByElection, deleteCandidates } from "../services/CandidateService";

function Candidates() {
    const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 768);
    const [candidates, setCandidates] = useState([]);
    const [elections, setElections] = useState([]);
    const [selectedElection, setSelectedElection] = useState("");
    const [isSelecting, setIsSelecting] = useState(false);
    const [selectedCandidates, setSelectedCandidates] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        // Handle responsive sidebar on window resize
        const handleResize = () => {
            setIsSidebarOpen(window.innerWidth >= 768);
        };

        window.addEventListener('resize', handleResize);

        // Listen for sidebar toggle events from Sidebar component
        const handleSidebarToggle = (event) => {
            if (event.detail && typeof event.detail.expanded === "boolean") {
                setIsSidebarOpen(event.detail.expanded);
            }
        };

        window.addEventListener("sidebarToggle", handleSidebarToggle);

        // Cleanup listeners
        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener("sidebarToggle", handleSidebarToggle);
        };
    }, []);

    useEffect(() => {
        getAllElections()
            .then((response) => setElections(response))
            .catch(() => toast.error("Failed to load elections"));
    }, []);

    useEffect(() => {
        if (selectedElection) {
            fetchCandidates(selectedElection);
        }
    }, [selectedElection]);

    const toggleSidebar = () => {
        const newState = !isSidebarOpen;
        setIsSidebarOpen(newState);
        // Dispatch the custom event to sync with Sidebar component
        window.dispatchEvent(
            new CustomEvent("sidebarToggle", { detail: { expanded: newState } })
        );
    };

    const fetchCandidates = async (electionId) => {
        try {
            const data = await getCandidatesByElection(electionId);
            setCandidates(data);
            toast.success("Candidate details loaded successfully.");
        } catch {
            toast.error("Failed to load candidate details.");
        }
    };

    const handleElectionChange = (e) => {
        setSelectedElection(e.target.value);
    };

    const handleDeleteSelectedDB = async () => {
        if (selectedCandidates.length === 0) {
            toast.error("Please select candidates to delete.");
            return;
        }
        try {
            await deleteCandidates(selectedCandidates);
            toast.success("Selected candidates deleted successfully.");
            fetchCandidates(selectedElection);
            setSelectedCandidates([]);
            setIsSelecting(false);
            setSelectAll(false);
        } catch {
            toast.error("Failed to delete candidates.");
        }
    };

    const handleEditCandidate = (candidate) => {
        navigate("/RegisterCandidatePage?edit=true", { state: candidate });
    };

    const handleSelectClick = () => {
        setIsSelecting(!isSelecting);
        setSelectedCandidates([]);
        setSelectAll(false);
    };

    const handleCheckboxChange = (candidateId) => {
        setSelectedCandidates((prevSelected) =>
            prevSelected.includes(candidateId)
                ? prevSelected.filter((id) => id !== candidateId)
                : [...prevSelected, candidateId]
        );
    };

    const handleSelectAll = () => {
        if (selectAll) {
            setSelectedCandidates([]);
        } else {
            setSelectedCandidates(candidates.map((candidate) => candidate.empId));
        }
        setSelectAll(!selectAll);
    };

    const EmptyState = () => (
        <div className="flex flex-col items-center justify-center p-4 md:p-10 text-center">
            <div className="bg-blue-50 rounded-full p-4 mb-4">
                <AlertCircle size={40} className="text-blue-500" />
            </div>
            <h3 className="text-xl font-semibold mb-2">No Candidates Found</h3>
            <p className="text-gray-600 mb-6 max-w-md">
                Create your first candidate to start managing your election.
            </p>
            <Link
                to="/RegisterCandidatePage"
                className="bg-blue-500 text-white px-4 py-2 md:px-6 md:py-3 rounded-full flex items-center hover:bg-blue-600 transition shadow-md text-sm md:text-base"
            >
                Add New Candidate <CircleCheck className="ml-2" size={window.innerWidth < 768 ? 16 : 20} />
            </Link>
        </div>
    );

    const handleSortCandidates = () => {
        setCandidates((prevCandidates) =>
            [...prevCandidates].sort((a, b) =>
                a.nameOfCandidate.localeCompare(b.nameOfCandidate)
            )
        );
    };

    // Updated contentClass to properly center content when sidebar is collapsed
    const contentClass = isSidebarOpen
        ? "sm:ml-64 w-full sm:w-[calc(100%-16rem)]"
        : "ml-16 w-[calc(100%-4rem)] md:w-[calc(100%-4rem)] mx-auto flex justify-center";

    // Inner container class for centering content
    const innerContainerClass = !isSidebarOpen
        ? "w-full max-w-4xl"
        : "w-full";

    return (
        <div className="flex flex-col h-screen bg-bgColor relative">
            <TopBar>
                <button
                    onClick={toggleSidebar}
                    className="p-2 mr-2"
                    aria-label="Toggle sidebar"
                >
                    <Menu size={24} />
                </button>
            </TopBar>

            <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

            <div className={`flex-1 transition-all duration-300 mt-16 ${contentClass}`}>
                <div className={`p-3 md:p-10 h-[calc(100vh-4rem)] overflow-auto ${innerContainerClass}`}>
                    <div className="flex flex-col md:flex-row md:items-center justify-between mb-5 space-y-3 md:space-y-0">
                        <div className="mt-2 md:mt-5">
                            <div className="flex items-center space-x-2">
                                <h2 className="text-xl md:text-2xl font-bold">Candidates</h2>
                                <div className="flex items-center text-lg md:text-2xl font-bold text-gray-600">
                                    <Users className="w-5 h-5 md:w-6 md:h-6 mr-1 stroke-[2]" />
                                    <span>{candidates.length}</span>
                                </div>
                            </div>
                        </div>
                        <select
                            value={selectedElection}
                            onChange={handleElectionChange}
                            className="border p-2 rounded-md shadow-md text-gray-700 w-full md:w-auto text-sm md:text-base"
                        >
                            <option value="">-- Select Election --</option>
                            {elections.map((election) => (
                                <option key={election.id} value={election.id}>
                                    {election.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="bg-white rounded-2xl shadow-lg mx-auto w-full p-2 md:p-3 relative">
                        {candidates.length === 0 ? (
                            <EmptyState />
                        ) : (
                            <>
                                {/* Header: Left-aligned Select All; Right-aligned action buttons */}
                                <div className="flex flex-col sm:flex-row justify-between items-center p-2 border-b space-y-2 sm:space-y-0">
                                    <div className="flex items-center self-start sm:self-auto">
                                        {isSelecting && (
                                            <>
                                                <span className="text-xs md:text-sm mr-2">Select All</span>
                                                <div
                                                    className={`w-4 h-4 md:w-5 md:h-5 flex justify-center items-center rounded-full border-2 cursor-pointer ${
                                                        selectAll ? "bg-blue-500 border-blue-500 text-white" : "border-gray-400"
                                                    }`}
                                                    onClick={handleSelectAll}
                                                >
                                                    {selectAll && <CircleCheck size={16} className="text-white" />}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                    <div className="flex items-center space-x-2 self-end sm:self-auto">
                                        <button
                                            onClick={handleSelectClick}
                                            className="text-blue-700 border border-blue-500 px-2 py-1 md:px-3 md:py-1 rounded-xl bg-blue-100 hover:bg-blue-500 hover:text-white transition text-xs md:text-sm"
                                        >
                                            {isSelecting ? "Cancel" : "Select"}
                                        </button>
                                        {isSelecting && (
                                            <button
                                                onClick={handleDeleteSelectedDB}
                                                className="text-red-600 border border-red-500 px-2 py-1 md:px-3 md:py-1 rounded-xl bg-red-100 hover:bg-red-500 hover:text-white transition flex items-center text-xs md:text-sm"
                                            >
                                                <Trash2 size={14} className="mr-1" /> Delete
                                            </button>
                                        )}
                                        <button
                                            onClick={handleSortCandidates}
                                            className="text-gray-600 border border-gray-400 px-2 py-1 md:px-3 md:py-1 rounded-xl bg-gray-200 hover:bg-gray-400 hover:text-white transition flex items-center text-xs md:text-sm"
                                        >
                                            <SortAsc size={14} className="mr-1" /> Sort
                                        </button>
                                    </div>
                                </div>
                                {/* Candidates List with increased vertical padding */}
                                <div className="overflow-y-auto max-h-[50vh] md:max-h-[500px] custom-scrollbar w-full">
                                    {candidates.map((candidate) => (
                                        <div
                                            key={candidate.empId}
                                            className="cursor-pointer flex items-center justify-between py-3 px-2 md:py-4 md:px-3 border-b w-full"
                                            onDoubleClick={() => handleEditCandidate(candidate)}
                                        >
                                            <div className="flex items-center w-full">
                                                {isSelecting && (
                                                    <div
                                                        className={`w-4 h-4 md:w-5 md:h-5 flex justify-center items-center rounded-full border-2 cursor-pointer mr-2 md:mr-3 ${
                                                            selectedCandidates.includes(candidate.empId)
                                                                ? "bg-blue-500 border-blue-500 text-white"
                                                                : "border-gray-400"
                                                        }`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleCheckboxChange(candidate.empId);
                                                        }}
                                                    >
                                                        {selectedCandidates.includes(candidate.empId) && (
                                                            <CircleCheck size={16} className="text-white" />
                                                        )}
                                                    </div>
                                                )}
                                                <CandidateList
                                                    image={candidate.imageUrl}
                                                    name={candidate.nameOfCandidate}
                                                    designation={candidate.currentDesignation}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {/* Bottom Action Buttons: Improved for mobile */}
                                <div className="flex flex-col sm:flex-row items-center justify-between mt-4 space-y-3 sm:space-y-0 px-2">
                                    <Link to="/RegisterCandidatePage" className="text-blue-500 text-xs md:text-sm self-start">
                                        + Add Candidate
                                    </Link>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
            <BottomBar />
            <Toaster />
        </div>
    );
}

export default Candidates;